import { Bid, BidRegistry, ArchitecturalDocument, UserMetrics, ReportFormatInfo } from '../models/bid';
import { handleResponse } from './errorHandler';
import AccessTokenManager from './accessTokenManager';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export const bidService = {
  /**
   * Upload one or more contractor bid PDFs.
   * - Single PDF: assumed to be the bid (no LLM classification).
   * - Multiple PDFs: backend classifies each as bid/plans/appraisal and links them.
   * @param files - PDF file or array of PDF files
   * @param ownerUserId - Admin-only: create the bid on behalf of this customer
   *   (their Auth0 sub). The bid then lives in that customer's account. Ignored
   *   by the backend for non-admin callers.
   * @returns Bid object with processing status
   */
  async uploadBid(files: File | File[], ownerUserId?: string): Promise<{ status: string; bid: Bid }> {
    try {
      const token = AccessTokenManager.getAccessToken();

      if (!token) {
        throw new Error('No auth token available');
      }

      const fileList = Array.isArray(files) ? files : [files];
      if (fileList.length === 0) {
        throw new Error('No files provided');
      }

      for (const file of fileList) {
        if (file.type !== 'application/pdf') {
          throw new Error(`Only PDF files are accepted (rejected: ${file.name})`);
        }
      }

      const formData = new FormData();
      fileList.forEach(file => formData.append('file', file));
      if (ownerUserId) {
        formData.append('ownerUserId', ownerUserId);
      }

      const response = await fetch(`${API_BASE_URL}/bids`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Bid upload error:', error);
      throw error;
    }
  },

  /**
   * Get bid details and extraction status
   * Poll this endpoint to check extraction progress
   * @param bidId - Bid UUID
   * @returns Complete bid object with extracted data if available
   */
  async getBid(bidId: string): Promise<Bid> {
    try {
      const token = AccessTokenManager.getAccessToken();

      if (!token) {
        throw new Error('No auth token available');
      }

      const response = await fetch(`${API_BASE_URL}/bids/${bidId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Get bid error:', error);
      throw error;
    }
  },

  /**
   * Get list of all bids for the authenticated user
   * @returns Array of bid registry items
   */
  async getBidRegistry(): Promise<BidRegistry> {
    try {
      const token = AccessTokenManager.getAccessToken();

      if (!token) {
        throw new Error('No auth token available');
      }

      const response = await fetch(`${API_BASE_URL}/bids`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Get bid registry error:', error);
      throw error;
    }
  },

  /**
   * Poll a bid until extraction is complete or failed
   * @param bidId - Bid UUID
   * @param onProgress - Optional callback for progress updates
   * @param pollInterval - Polling interval in milliseconds (default: 3000ms)
   * @returns Promise that resolves when extraction is complete
   */
  async pollBidStatus(
    bidId: string,
    onProgress?: (bid: Bid) => void,
    pollInterval: number = 3000
  ): Promise<Bid> {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const bid = await bidService.getBid(bidId);

          // Call progress callback if provided
          if (onProgress) {
            onProgress(bid);
          }

          // Check if processing is complete
          // Status flow: processing → raw_extracted → formatted → analyzing → analyzed (or failed)
          if (bid.status === 'analyzed' || bid.status === 'failed') {
            resolve(bid);
            return;
          }

          // Continue polling
          setTimeout(poll, pollInterval);
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  },

  /**
   * Get a signed URL to view the original bid document
   * @param bidId - Bid UUID
   * @returns Signed URL to the PDF document
   */
  async getBidDocument(bidId: string): Promise<string> {
    try {
      const token = AccessTokenManager.getAccessToken();

      if (!token) {
        throw new Error('No auth token available');
      }

      const response = await fetch(`${API_BASE_URL}/bids/${bidId}/document`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = await handleResponse(response);
      return result.documentUrl;
    } catch (error) {
      console.error('Get bid document error:', error);
      throw error;
    }
  },

  /**
   * Trigger structural analysis of an extracted bid
   * @param bidId - Bid UUID
   * @returns Analysis start confirmation
   */
  async analyzeBid(bidId: string): Promise<{ status: string; bidId: string; message: string }> {
    try {
      const token = AccessTokenManager.getAccessToken();

      if (!token) {
        throw new Error('No auth token available');
      }

      const response = await fetch(`${API_BASE_URL}/bids/${bidId}/analyze`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Analyze bid error:', error);
      throw error;
    }
  },

  /**
   * Get or generate a PDF report for an analyzed bid
   * @param bidId - Bid UUID
   * @returns Report URL and metadata
   */
  async getBidReport(bidId: string): Promise<{ reportUrl: string; reportKey: string; cached: boolean }> {
    try {
      const token = AccessTokenManager.getAccessToken();

      if (!token) {
        throw new Error('No auth token available');
      }

      const response = await fetch(`${API_BASE_URL}/bids/${bidId}/report`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Get bid report error:', error);
      throw error;
    }
  },

  async uploadArchitecturalDocument(file: File): Promise<{ status: string; architecturalDocument: ArchitecturalDocument }> {
    try {
      const token = AccessTokenManager.getAccessToken();
      if (!token) throw new Error('No auth token available');

      if (file.type !== 'application/pdf') {
        throw new Error('Only PDF files are accepted');
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE_URL}/architectural-documents`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Architectural document upload error:', error);
      throw error;
    }
  },

  async getArchitecturalDocument(archDocId: string): Promise<ArchitecturalDocument> {
    try {
      const token = AccessTokenManager.getAccessToken();
      if (!token) throw new Error('No auth token available');

      const response = await fetch(`${API_BASE_URL}/architectural-documents/${archDocId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Get architectural document error:', error);
      throw error;
    }
  },

  /**
   * Get a signed URL to download the original architectural document (PDF).
   * @param archDocId - Architectural document UUID
   * @returns Signed URL to the PDF
   */
  async getArchitecturalDocumentUrl(archDocId: string): Promise<string> {
    try {
      const token = AccessTokenManager.getAccessToken();
      if (!token) throw new Error('No auth token available');

      const response = await fetch(`${API_BASE_URL}/architectural-documents/${archDocId}/document`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = await handleResponse(response);
      return result.documentUrl;
    } catch (error) {
      console.error('Get architectural document URL error:', error);
      throw error;
    }
  },

  /**
   * Re-run the linked plans and re-analyze the bid. Server-driven: the bid flips to
   * 'analyzing' immediately and the extract → re-analyze chain runs server-side, so a
   * refresh just resumes polling. Caller should poll the bid until analyzed/failed.
   * @param bidId - Bid UUID
   */
  async rerunPlans(bidId: string): Promise<{ status: string; bid: { id: string; status: string } }> {
    try {
      const token = AccessTokenManager.getAccessToken();
      if (!token) throw new Error('No auth token available');

      const response = await fetch(`${API_BASE_URL}/bids/${bidId}/rerun-plans`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Re-run plans error:', error);
      throw error;
    }
  },

  async linkArchitecturalDocument(bidId: string, architecturalDocumentId: string): Promise<any> {
    try {
      const token = AccessTokenManager.getAccessToken();
      if (!token) throw new Error('No auth token available');

      const response = await fetch(`${API_BASE_URL}/bids/${bidId}/link-architectural-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ architecturalDocumentId }),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Link architectural document error:', error);
      throw error;
    }
  },

  /**
   * Admin: list any user's bid registry by userId.
   * Caller must have ADMIN role on their JWT or this will 403.
   * @param userId - Auth0 sub of the target user
   */
  async getUserBidRegistry(userId: string): Promise<BidRegistry> {
    try {
      const token = AccessTokenManager.getAccessToken();
      if (!token) throw new Error('No auth token available');

      const response = await fetch(`${API_BASE_URL}/bids/users/${encodeURIComponent(userId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Get user bid registry error:', error);
      throw error;
    }
  },

  /**
   * Admin: usage metrics aggregated from a target user's bid headers.
   * Caller must have ADMIN role on their JWT or this will 403.
   * @param userId - Auth0 sub of the target user
   */
  async getUserMetrics(userId: string): Promise<UserMetrics> {
    try {
      const token = AccessTokenManager.getAccessToken();
      if (!token) throw new Error('No auth token available');

      const response = await fetch(`${API_BASE_URL}/bids/metrics/${encodeURIComponent(userId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Get user metrics error:', error);
      throw error;
    }
  },

  /**
   * Admin: get a user's report format and the list of selectable formats.
   * Caller must have ADMIN role on their JWT or this will 403.
   * @param userId - Auth0 sub of the target user
   */
  async getReportFormat(userId: string): Promise<ReportFormatInfo> {
    try {
      const token = AccessTokenManager.getAccessToken();
      if (!token) throw new Error('No auth token available');

      const response = await fetch(`${API_BASE_URL}/bids/report-format/${encodeURIComponent(userId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Get report format error:', error);
      throw error;
    }
  },

  /**
   * Admin: set a user's report format.
   * Caller must have ADMIN role on their JWT or this will 403.
   * @param userId - Auth0 sub of the target user
   * @param format - a format key from availableFormats
   */
  async setReportFormat(userId: string, format: string): Promise<ReportFormatInfo> {
    try {
      const token = AccessTokenManager.getAccessToken();
      if (!token) throw new Error('No auth token available');

      const response = await fetch(`${API_BASE_URL}/bids/report-format/${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ format }),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Set report format error:', error);
      throw error;
    }
  },

  /**
   * Admin/owner: re-run only the feasibility report (Phase 4) — refreshes the
   * assessment + summary without re-extracting or re-estimating line items.
   */
  async reanalyzeFeasibility(bidId: string): Promise<{ status: string; bidId: string; message: string }> {
    try {
      const token = AccessTokenManager.getAccessToken();
      if (!token) throw new Error('No auth token available');

      const response = await fetch(`${API_BASE_URL}/bids/${bidId}/reanalyze-feasibility`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Reanalyze feasibility error:', error);
      throw error;
    }
  },

  async pollArchDocStatus(
    archDocId: string,
    onProgress?: (doc: ArchitecturalDocument) => void,
    pollInterval: number = 3000
  ): Promise<ArchitecturalDocument> {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const doc = await bidService.getArchitecturalDocument(archDocId);
          if (onProgress) onProgress(doc);

          if (doc.status === 'extracted' || doc.status === 'failed') {
            resolve(doc);
            return;
          }

          setTimeout(poll, pollInterval);
        } catch (error) {
          reject(error);
        }
      };
      poll();
    });
  }
};
